import React, { useEffect, useState } from 'react';

import { Box } from '@rbilabs/universal-components';
import { PermissionStatus, useCameraPermissions } from 'expo-camera';
import { useIntl } from 'react-intl';
import { Platform, StyleSheet } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import ConfirmDialog from 'components/confirm-dialog';
import Dialog from 'components/dialog';
import { primitive } from 'styles/constants/primitives';
import { openPushSetting as openDeviceSettings } from 'utils/permissions';

import { CameraScreen } from './camera-screen';
import { PhotoReviewScreen } from './photo-review-screen';

enum Mode {
  Camera = 'camera',
  Review = 'review',
}

export enum FlipMode {
  Front = 'front',
  Back = 'back',
}

type CameraProps = {
  onClose: () => void;
  callback: (picture: string) => void;
  flip: FlipMode;
};

export const Camera = ({ onClose, callback, flip }: CameraProps) => {
  const { formatMessage } = useIntl();
  const [mode, setMode] = useState(Mode.Camera);
  const [picture, setPicture] = useState('');
  const [pictureBase64, setPictureBase64] = useState('');
  const [permission, requestPermission] = useCameraPermissions();
  const [showAllowCameraAccessModal, setShowAllowCameraAccessModal] = useState(false);

  useEffect(() => {
    if (permission?.status === PermissionStatus.UNDETERMINED) {
      requestPermission();
    } else if (permission?.status === PermissionStatus.DENIED) {
      setShowAllowCameraAccessModal(true);
    }
  }, [permission, requestPermission, showAllowCameraAccessModal]);

  const updateAllowCameraAccess = () => {
    setShowAllowCameraAccessModal(false);
    onClose();
  };

  const updateMode = (mode: Mode) => {
    setMode(mode);
  };

  const onTakePhoto = ({ base64 = '', uri = '' }) => {
    setPicture(uri);
    setPictureBase64(base64);
    updateMode(Mode.Review);
  };

  const submitPhoto = async (photo: string) => {
    callback(photo);
  };

  const backgroundColorScreen: string =
    Platform.OS === 'web' ? primitive.$blackOpacity70 : primitive.$black;

  return (
    <SafeAreaView style={[styles.absoluteFill, { backgroundColor: backgroundColorScreen }]}>
      <Box flexDirection={'column'} justifyContent="center" alignItems="center">
        {mode === Mode.Camera ? (
          <CameraScreen flip={flip} onTakePhoto={onTakePhoto} onClose={onClose} />
        ) : (
          <PhotoReviewScreen
            value={{ picture, pictureBase64 }}
            onClose={onClose}
            onRetake={() => updateMode(Mode.Camera)}
            onSubmit={submitPhoto}
            flip={flip}
          />
        )}
      </Box>
      {Platform.OS !== 'web' && (
        <ConfirmDialog
          showDialog={showAllowCameraAccessModal}
          heading={formatMessage({ id: 'allowCameraAccess' })}
          body={formatMessage({ id: 'goToDeviceSettings' })}
          cancelLabel={formatMessage({ id: 'openDeviceSettings' })}
          confirmLabel={formatMessage({ id: 'notNow' })}
          onDismiss={updateAllowCameraAccess}
          onCancel={updateAllowCameraAccess}
          onConfirm={() => {
            openDeviceSettings();
            updateAllowCameraAccess();
          }}
          modalAppearanceEventMessage="Confirm Open device Camera Access settings"
        />
      )}
      {Platform.OS === 'web' && (
        <Dialog
          showDialog={showAllowCameraAccessModal}
          modalAppearanceEventMessage={formatMessage({ id: 'allowCameraAccessWebTitle' })}
          heading={formatMessage({ id: 'allowCameraAccessWebTitle' })}
          body={formatMessage({ id: 'allowCameraAccessWebText' })}
          accessibilityLabel={formatMessage({ id: 'allowCameraAccessWebText' })}
          closeButtonLabelId={formatMessage({ id: 'notNow' })}
          showCloseButton
          onDismiss={() => {
            updateAllowCameraAccess();
          }}
        />
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  absoluteFill: {
    ...StyleSheet.absoluteFillObject,
  },
});
